import LazyImage from "../../../../../components/app-specific/lazy-image";
import {Popover} from "../../../../../components/app-specific/popover";
import IconButton from "../../../../../components/base/button/icon_button";
import {SvgIcon} from "@mui/material";
import {MoreHoriz} from "@mui/icons-material";
import classnames from "classnames";
import Button from "../../../../../components/base/button/button";
import React, {useCallback, useMemo} from "react";
import {ReactComponent as ImagePlaceholder} from "../../../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import DateUtils from "../../../../../../core/services/date-utils";
import useNoteBookTagsInfo from "../../../../../hooks/use-note-book-tags-info";
import {Link} from 'react-router-dom';
import Chips, {ChipsContentType, ChipsTypes} from "../../../../../components/base/chips/chips";
import CacheService from "../../../../../../core/services/cache/cache_service";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";

const NotebookTaskCard = ({task, disabled, onRemove, onEdit, toggleTaskDoneStatus, setTask}) => {
    const {pageInfo, labelTags, loading} = useNoteBookTagsInfo(task?.tags);

    const operatorInformation = useOperatorInformation();
    const canManage = useMemo(() => task.createdByOperator?.id === operatorInformation?.id, [task.createdByOperator?.id, operatorInformation?.id]);
    const createdByProfileImageUrl = useMemo(() => CacheService.getOperatorProfileImage(task?.createdByOperator?.id), [task?.createdByOperator?.id]);

    const dispatch = useDispatch();

    /**
     * Navigates to the page the task is linked to.
     * @type {(function(): void)}
     */
    const onLinkClicked = useCallback(() => {
        window.open(task?.page?.url ?? '', '_blank');
    }, [task?.page?.url]);

    /**
     * Opens the task more info dialog
     */
    const openTaskFullInfoDialog = useCallback((close) => {
        close();
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.taskMoreInformation, {
            open: true,
            task: task,
            toggleTaskDoneStatus: toggleTaskDoneStatus,
            setTask: setTask,
            page: null
        }))
    }, [dispatch, setTask, task, toggleTaskDoneStatus]);

    return (
        <div className={'task-card-medium'}>
            <div className={'task-header'}>
                <p className={'task-title'}>
                    {task?.title}
                </p>
                {
                    canManage &&
                    <Popover
                        elevation={2}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        child={(onEnter) =>
                            <IconButton
                                onPressed={onEnter}
                                disabled={disabled}
                                color={'paper.secondary'}
                            >
                                <SvgIcon component={MoreHoriz} className='icon small'/>
                            </IconButton>
                        }
                        popoverChild={(_, close) => (
                            <div className={'address-card-popover-actions'}>
                                <div
                                    className={classnames('action-item')}
                                    onClick={() => openTaskFullInfoDialog(close)}
                                >
                                    <p>View Info</p>
                                </div>
                                <div
                                    className={classnames('action-item')}
                                    onClick={() => onEdit(task, close)}
                                >
                                    <p>Edit</p>
                                </div>
                                <div
                                    className={classnames('action-item', 'remove')}
                                    onClick={() => onRemove(task, close)}
                                >
                                    <p>Remove</p>
                                </div>
                            </div>
                        )}
                    />
                }
            </div>
            {
                ((canManage && !!task?.assignedToOperator) || (!canManage && !!task?.createdByOperator)) &&
                <div className={classnames('assigned-container')}>
                    <div className={'assigner-image-container'}>
                        <LazyImage
                            alt={'assigner image'}
                            noImage={<ImagePlaceholder/>}
                            src={createdByProfileImageUrl}
                            height={27}
                        />
                    </div>
                    <p className={'assigner-text'}>
                        {
                            canManage
                                ? <>
                                    Assigned to <span className={'assigner-name'}>
                                {
                                    task?.assignedToOperator?.id === operatorInformation?.id
                                        ? "me"
                                        : (task?.assignedToOperator?.firstname ?? '') + ' ' + (task?.assignedToOperator?.lastname ?? '')
                                }
                            </span>
                                </>
                                : <>
                                    Assigned by <span className={'assigner-name'}>
                                {
                                    (task?.createdByOperator?.firstname ?? '') + ' ' + (task?.createdByOperator?.lastname ?? '')
                                }
                            </span>
                                </>
                        }
                    </p>
                </div>
            }
            <div className={'task-description'}>
                <p>
                    {task.description ?? ''}
                </p>
            </div>
            {
                !!task?.dueDate &&
                <div className={'due-date-wrapper'}>
                    <div
                        className={classnames('due-date', {'passed': DateUtils.server(task?.dueDate).isBefore(DateUtils.serverNow())})}>
                        <p>
                            Due date: <span className={'date'}>{DateUtils.serverToLocal(task?.dueDate).format(DateUtils.dateFormat)}</span>
                        </p>
                    </div>
                </div>
            }
            {
                !!task?.completionDate &&
                <div className={'due-date-wrapper mt-2'}>
                    <div
                        className={classnames('due-date')}>
                        <p>
                            Completion date: <span
                            className={'date'}>{DateUtils.serverToLocal(task?.completionDate).format(DateUtils.dateFormat)}</span>
                        </p>
                    </div>
                </div>
            }
            {
                (!!labelTags?.length) &&
                <>
                    <div className={'mt-2 text-sm font-weight-semi-bold'}>
                        Tags:
                    </div>
                    <div className={'d-flex flex-wrap my-2'}>
                        {
                            labelTags.map((tag) => (
                                <Chips
                                    key={tag.key}
                                    type={ChipsTypes.none}
                                    contentType={ChipsContentType.text}
                                    content={`${tag.key}: ${tag.value ?? ""}`}
                                    className={'mr-2 mb-2'}
                                />
                            ))
                        }
                    </div>
                </>
            }
            {
                (!!task.page) &&
                <Button
                    variant={'text'}
                    color={'primary'}
                    className={'link-button mt-1'}
                    onClick={onLinkClicked}
                >
                    {task.page?.title}
                </Button>
            }
            {
                pageInfo &&
                <Button
                    variant={'text'}
                    color={'primary'}
                    className={'link-button mt-1'}
                    component={pageInfo.url ? Link : 'button'}
                    {...(pageInfo.url && {
                        to: pageInfo.url,
                        target: '_blank',
                    })}
                    {...(!pageInfo.url && {
                        onClick: pageInfo.action,
                    })}
                >
                    {
                        loading
                            ? "Navigating..."
                            : pageInfo.title
                    }
                </Button>
            }
        </div>
    )
}
export default NotebookTaskCard;
